/**
 * Pattern Loop Generator
 *
 * Implements the Advanced pattern-based loop generation strategy.
 * Uses the coin-change population engine to create musically coherent loops.
 *
 * Ticket 20260319V: Extracted fetchAndConcatenateAudio shared helper
 * Ticket 20260326g: Added generateNewPatternLoop (coin-change generator)
 * Ticket 20260326h: Removed legacy generatePatternLoop and generateFullSong
 * Ticket 20260330a: Updated to handle stereo audio via DecompressedAudio
 */

import { QdrantService } from '../services/qdrantService';
import type { SampleMeta } from './populationEngine';
import { buildSizePool, computeAchievableTargets, generateAndPopulate, populateFixedPattern, snapToMusicalBars } from './populationEngine';
import { decompressAudio, concatenateAudio, type AudioPayload, type DecompressedAudio } from '../audio/audioUtils';
import type { LoopResult } from './types';
import { barTimeCalc } from '../music/musicTheory';
import { DEFAULT_PULSE_COMPAS, DEFAULT_TIME_REFERENCE, COIN_CHANGE_TOLERANCE, COIN_CHANGE_MAX_COMBOS, PERIODIC_FILL_ENABLED, PERIODIC_FILL_EVERY, PERIODIC_FILL_PROBABILITY } from '../config/music.config';
import type { SizeCategory } from '../config/music.config';
import { track } from '../analytics/analyticsService';

const isDev = import.meta.env.DEV;

// ---------------------------------------------------------------------------
// Shared internal helpers
// ---------------------------------------------------------------------------

/** Max file names listed in a MissingAudioError message and the analytics event. */
const MISSING_AUDIO_LIST_LIMIT = 5;

/**
 * Thrown when Qdrant has no audio point for one or more planned files.
 * Playing without them would shorten the loop and break the bar grid.
 * Ticket 20260923d.
 */
export class MissingAudioError extends Error {
    constructor(public readonly album: string, public readonly files: string[]) {
        super(
            `Missing audio for ${files.length} file(s) in ${album}: ` +
            files.slice(0, MISSING_AUDIO_LIST_LIMIT).join(', ') +
            (files.length > MISSING_AUDIO_LIST_LIMIT ? ', ...' : ''),
        );
        this.name = 'MissingAudioError';
    }
}

/**
 * Throw MissingAudioError (and report it to analytics) unless every planned
 * file has an audio payload. Files may repeat in the plan; they are reported once.
 */
function _assertAllAudioPresent(
    files: string[],
    audioMap: Map<string, AudioPayload>,
    albumName: string,
): void {
    const missing = [...new Set(files.filter(f => !audioMap.has(f)))];
    if (missing.length === 0) return;

    track('audio_missing', {
        album: albumName,
        missing_count: missing.length,
        files: missing.slice(0, MISSING_AUDIO_LIST_LIMIT),
    });
    throw new MissingAudioError(albumName, missing);
}

/**
 * Collect ordered files/tokens/durations from a PopulationResult, fetch the
 * corresponding audio from Qdrant, decompress, and concatenate into a single
 * DecompressedAudio object.
 *
 * Extracted to avoid repeating the identical block in generateNewPatternLoop,
 * generateCustomPatternLoop, and generateFromAlbum.
 */
async function _collectAndFetchAudio(
    result: { slots: Array<{ token: string; samples: Array<{ file: string; bars: number }> }> },
    barTime: number,
    albumName: string,
    qdrantService: QdrantService,
): Promise<{
    allFiles: string[];
    allTokens: string[];
    allDurations: number[];
    loopSlots: Array<{ token: string; duration: number }>;
    concatenated: DecompressedAudio;
    sampleRate: number;
    actualDuration: number;
}> {
    const allFiles: string[] = [];
    const allTokens: string[] = [];
    const allDurations: number[] = [];
    const loopSlots: Array<{ token: string; duration: number }> = [];

    for (const slot of result.slots) {
        let slotDuration = 0;
        for (const s of slot.samples) {
            allFiles.push(s.file);
            allTokens.push(slot.token);
            const dur = s.bars * barTime;
            allDurations.push(dur);
            slotDuration += dur;
        }
        loopSlots.push({ token: slot.token, duration: slotDuration });
    }

    const audioPayloads = await qdrantService.getAudioByFilesAndAlbums(
        allFiles.map(file => ({ file, album: albumName })),
    );
    const audioMap = new Map(audioPayloads.map((p: AudioPayload) => [p.file, p]));

    if (allFiles.length === 0) {
        throw new Error('No files in generated pattern');
    }
    _assertAllAudioPresent(allFiles, audioMap, albumName);

    const audioSegments: DecompressedAudio[] = allFiles.map(file => decompressAudio(audioMap.get(file)!));
    const concatenated = concatenateAudio(audioSegments);
    const sampleRate = audioPayloads[0].sample_rate;
    const actualDuration = concatenated.length / sampleRate;

    return { allFiles, allTokens, allDurations, loopSlots, concatenated, sampleRate, actualDuration };
}

/**
 * Shared helper: fetch audio for a list of tokens from Qdrant and concatenate into a single DecompressedAudio.
 *
 * Used by loadSavedLoop (library reload).
 *
 * @param tokens - Ordered list of file tokens (e.g. ["A1", "B2", "C3"])
 * @param album  - Album name all tokens belong to
 * @param qdrantService - Qdrant service instance
 * @returns Concatenated audio data and sample rate
 * @throws MissingAudioError if any token has no audio in Qdrant
 */
export async function fetchAndConcatenateAudio(
    tokens: string[],
    album: string,
    qdrantService: QdrantService
): Promise<{ audioData: DecompressedAudio; sampleRate: number }> {
    const audioPayloads = await qdrantService.getAudioByFilesAndAlbums(
        tokens.map(file => ({ file, album }))
    );
    const audioMap = new Map(audioPayloads.map((p: AudioPayload) => [p.file, p]));

    if (tokens.length === 0) {
        throw new Error(`No tokens to load for album: ${album}`);
    }
    _assertAllAudioPresent(tokens, audioMap, album);

    const audioSegments = tokens.map(f => decompressAudio(audioMap.get(f)!));

    return { audioData: concatenateAudio(audioSegments), sampleRate: audioPayloads[0].sample_rate };
}

const SIZE_CATEGORY_TO_MODE: Record<SizeCategory, string> = {
    SHORT:  'standard',
    MID:    'creative',
    LARGE:  'crazy',
    CUSTOM: 'standard', // Custom mode uses its own pattern — mode value is unused
};

/**
 * Maximum number of albums to try before giving up.
 * Albums are shuffled on each call so the order varies.
 * Ticket 20260412a.
 */
const MAX_ALBUM_ATTEMPTS = 5;

/**
 * Generate a full song loop using the coin-change population engine.
 *
 * Ticket 20260412a: tries up to MAX_ALBUM_ATTEMPTS different albums before failing.
 * Albums are shuffled on each call so the order varies.
 *
 * Flow (per album candidate):
 *  1. Fetch albums for styleGroup from Qdrant
 *  2. Shuffle and slice up to MAX_ALBUM_ATTEMPTS candidates
 *  3. For each candidate album:
 *     a. Fetch sample metadata (file, token, duration, bpm)
 *     b. Compute bars per sample: bars = duration / barTimeCalc(bpm)
 *     c. Build tokenSamples: Record<string, SampleMeta[]>
 *     d. Compute validBars per token: computeAchievableTargets() per token pool
 *     e. Derive style from album genre, mode from sizeCategory
 *     f. Call generateAndPopulate(...)
 *     g. On success: fetch audio, decompress, concatenate, return LoopResult
 *     h. On failure: log warning, try next album
 *  4. If all candidates exhausted: throw Error
 */
export async function generateNewPatternLoop(
    styleGroup: string,
    sizeCategory: SizeCategory,
    qdrantService: QdrantService,
): Promise<LoopResult> {
    if (isDev) { console.log('[NewPatternLoop] Generating loop for style group:', styleGroup, 'size:', sizeCategory); }

    // 1. Fetch albums for styleGroup
    const albums = await qdrantService.getAlbumsByStyleGroup(styleGroup);
    if (albums.length === 0) {
        throw new Error(`No albums found for style group: ${styleGroup}`);
    }

    // 2. Shuffle and slice up to MAX_ALBUM_ATTEMPTS candidates
    const candidates = [...albums]
        .sort(() => Math.random() - 0.5)
        .slice(0, MAX_ALBUM_ATTEMPTS);

    if (isDev) { console.log(`[NewPatternLoop] Will try up to ${candidates.length} album(s)`); }

    let lastError = 'unknown error';

    // 3. Try each candidate album in turn
    for (let i = 0; i < candidates.length; i++) {
        const album = candidates[i];
        if (isDev) { console.log(`[NewPatternLoop] Album attempt ${i + 1}/${candidates.length}: ${album.album} BPM:${album.bpm}`); }

        try {
            // a. Fetch sample metadata
            const samples = await qdrantService.getSamplesByAlbum(album.album);
            if (samples.length === 0) {
                lastError = `No samples found for album: ${album.album}`;
                if (isDev) { console.warn(`[NewPatternLoop] Skipping album (no samples): ${album.album}`); }
                continue;
            }

            // b. Compute bars per sample — snap to nearest musical fraction to absorb float drift
            const pulseCompas = album.pulse_compas ?? DEFAULT_PULSE_COMPAS;
            const barTime = barTimeCalc(album.bpm, pulseCompas, DEFAULT_TIME_REFERENCE);
            const sampleMetas: SampleMeta[] = samples
                .filter(s => s.duration > 0 && s.token)
                .map(s => ({
                    file:         s.file,
                    bars:         snapToMusicalBars(s.duration / barTime),
                    token:        s.token,
                    isFill:       s.isFill,
                    pulse_compas: pulseCompas,
                }));

            // c. Build tokenSamples
            const tokenSamples: Record<string, SampleMeta[]> = {};
            for (const sm of sampleMetas) {
                if (!tokenSamples[sm.token]) tokenSamples[sm.token] = [];
                tokenSamples[sm.token].push(sm);
            }
            if (isDev) { console.log('[NewPatternLoop] Token types:', Object.keys(tokenSamples)); }

            // d. Compute validBars per token
            const validBars: Record<string, number[]> = {};
            for (const [token, metas] of Object.entries(tokenSamples)) {
                const pool = buildSizePool(metas);
                validBars[token] = computeAchievableTargets(pool, 32, COIN_CHANGE_TOLERANCE);
            }

            // e. Derive style and mode
            const style = album.genre.toLowerCase();
            const mode = SIZE_CATEGORY_TO_MODE[sizeCategory];
            if (isDev) { console.log('[NewPatternLoop] style:', style, 'mode:', mode); }

            // f. Generate and populate
            const result = generateAndPopulate(
                style, mode, tokenSamples, 250, COIN_CHANGE_MAX_COMBOS, COIN_CHANGE_TOLERANCE, validBars,
                false, false,
                isDev,
                isDev ? { album: album.album, genre: album.genre, bpm: album.bpm } : undefined,
                PERIODIC_FILL_ENABLED,
                PERIODIC_FILL_EVERY,
                PERIODIC_FILL_PROBABILITY,
            );

            if (!result.complete) {
                lastError = `Population failed after ${result.attempts} attempts` +
                    (result.failedAt ? ` (stuck at ${result.failedAt})` : '');
                if (isDev) { console.warn(`[NewPatternLoop] Album ${album.album} failed: ${lastError}`); }
                continue;
            }

            if (isDev) {
                console.log(`[NewPatternLoop] Success on album attempt ${i + 1}/${candidates.length}:`, album.album);
                console.log('[NewPatternLoop] Population complete in', result.attempts, 'attempts, fallback:', result.fallbackUsed);
            }

            // g. Collect files, fetch audio, decompress and concatenate
            const { allFiles, allTokens, allDurations, loopSlots, concatenated, sampleRate, actualDuration } =
                await _collectAndFetchAudio(result, barTime, album.album, qdrantService);

            if (isDev) {
                console.log('[NewPatternLoop] Loop generated successfully');
                console.log('[NewPatternLoop] Structure:', allTokens.join(' -> '));
                console.log('[NewPatternLoop] Total duration:', actualDuration.toFixed(2), 'seconds');
            }

            return {
                audioData: concatenated,
                album: album.album,
                genre: album.genre,
                bpm: album.bpm,
                pulse_compas: pulseCompas,
                files: allFiles,
                segments: allFiles.map((file, i) => ({
                    file,
                    token: allTokens[i],
                    duration: allDurations[i],
                })),
                slots: loopSlots,
                totalDuration: actualDuration,
                mode: 'Advanced',
                sampleRate,
                trace: result.trace,
            };
        } catch (e) {
            // Any failure for one album (fetch, population, decode) moves on to the next candidate
            lastError = (e as Error).message;
            if (isDev) { console.warn(`[NewPatternLoop] Album ${album.album} threw:`, e); }
        }
    }

    // 4. All candidates exhausted
    throw new Error(
        `No valid loop found after trying ${candidates.length} album(s). Last error: ${lastError}`,
    );
}

/**
 * Generate a loop using a user-defined custom pattern.
 *
 * Same album/sample fetch flow as generateNewPatternLoop, but skips
 * generateSongPattern() entirely — uses the caller-supplied pattern directly.
 *
 * Ticket 20260412a: tries up to MAX_ALBUM_ATTEMPTS different albums before failing.
 *
 * @param styleGroup    - Style group name (used to pick album)
 * @param customPattern - Array of {section: bars} objects built by the user
 * @param qdrantService - Qdrant service instance
 */
export async function generateCustomPatternLoop(
    styleGroup: string,
    customPattern: Array<Record<string, number>>,
    qdrantService: QdrantService,
): Promise<LoopResult> {
    if (isDev) { console.log('[CustomPatternLoop] Generating loop for style group:', styleGroup, 'pattern:', customPattern); }

    if (customPattern.length === 0) {
        throw new Error('Custom pattern must have at least one section');
    }

    // 1. Fetch albums for styleGroup
    const albums = await qdrantService.getAlbumsByStyleGroup(styleGroup);
    if (albums.length === 0) {
        throw new Error(`No albums found for style group: ${styleGroup}`);
    }

    // 2. Shuffle and slice up to MAX_ALBUM_ATTEMPTS candidates
    const candidates = [...albums]
        .sort(() => Math.random() - 0.5)
        .slice(0, MAX_ALBUM_ATTEMPTS);

    if (isDev) { console.log(`[CustomPatternLoop] Will try up to ${candidates.length} album(s)`); }

    let lastError = 'unknown error';

    // 3. Try each candidate album in turn
    for (let i = 0; i < candidates.length; i++) {
        const album = candidates[i];
        if (isDev) { console.log(`[CustomPatternLoop] Album attempt ${i + 1}/${candidates.length}: ${album.album} BPM:${album.bpm}`); }

        try {
            // a. Fetch sample metadata
            const samples = await qdrantService.getSamplesByAlbum(album.album);
            if (samples.length === 0) {
                lastError = `No samples found for album: ${album.album}`;
                if (isDev) { console.warn(`[CustomPatternLoop] Skipping album (no samples): ${album.album}`); }
                continue;
            }

            // b. Compute bars per sample — snap to nearest musical fraction to absorb float drift
            const pulseCompas = album.pulse_compas ?? DEFAULT_PULSE_COMPAS;
            const barTime = barTimeCalc(album.bpm, pulseCompas, DEFAULT_TIME_REFERENCE);
            const sampleMetas: SampleMeta[] = samples
                .filter(s => s.duration > 0 && s.token)
                .map(s => ({
                    file:         s.file,
                    bars:         snapToMusicalBars(s.duration / barTime),
                    token:        s.token,
                    isFill:       s.isFill,
                    pulse_compas: pulseCompas,
                }));

            // c. Build tokenSamples
            const tokenSamples: Record<string, SampleMeta[]> = {};
            for (const sm of sampleMetas) {
                if (!tokenSamples[sm.token]) tokenSamples[sm.token] = [];
                tokenSamples[sm.token].push(sm);
            }

            // d. Populate using the custom pattern directly via populateFixedPattern()
            const result = populateFixedPattern(
                customPattern,
                tokenSamples,
                250,
                COIN_CHANGE_MAX_COMBOS,
                COIN_CHANGE_TOLERANCE,
                isDev,
                isDev ? { album: album.album, genre: album.genre, bpm: album.bpm } : undefined,
                PERIODIC_FILL_ENABLED,
                PERIODIC_FILL_EVERY,
                PERIODIC_FILL_PROBABILITY,
            );

            if (!result.complete) {
                lastError = `Population failed after ${result.attempts} attempts` +
                    (result.failedAt ? ` (stuck at ${result.failedAt})` : '');
                if (isDev) { console.warn(`[CustomPatternLoop] Album ${album.album} failed: ${lastError}`); }
                continue;
            }

            if (isDev) {
                console.log(`[CustomPatternLoop] Success on album attempt ${i + 1}/${candidates.length}:`, album.album);
            }

            // e. Collect files, fetch audio, decompress and concatenate
            const { allFiles, allTokens, allDurations, loopSlots, concatenated, sampleRate, actualDuration } =
                await _collectAndFetchAudio(result, barTime, album.album, qdrantService);

            if (isDev) {
                console.log('[CustomPatternLoop] Loop generated successfully');
                console.log('[CustomPatternLoop] Structure:', allTokens.join(' -> '));
                console.log('[CustomPatternLoop] Total duration:', actualDuration.toFixed(2), 'seconds');
            }

            return {
                audioData: concatenated,
                album: album.album,
                genre: album.genre,
                bpm: album.bpm,
                pulse_compas: pulseCompas,
                files: allFiles,
                segments: allFiles.map((file, i) => ({
                    file,
                    token: allTokens[i],
                    duration: allDurations[i],
                })),
                slots: loopSlots,
                totalDuration: actualDuration,
                mode: 'Custom',
                sampleRate,
                trace: result.trace,
            };
        } catch (e) {
            // Any failure for one album (fetch, population, decode) moves on to the next candidate
            lastError = (e as Error).message;
            if (isDev) { console.warn(`[CustomPatternLoop] Album ${album.album} threw:`, e); }
        }
    }

    // 4. All candidates exhausted
    throw new Error(
        `No valid loop found for custom pattern after trying ${candidates.length} album(s). Last error: ${lastError}`,
    );
}

/**
 * Generate a loop from a specific user-uploaded album already in the production library.
 *
 * Identical pipeline to generateNewPatternLoop() but skips the random album selection step.
 * The caller provides the exact librosa_min album name (e.g. "blues_70BPM_5f073160") as
 * well as the BPM and genre from the D1 record, so no Qdrant album lookup is needed.
 *
 * Used by the "My Albums" feature: user selects one of their approved uploads and
 * the generator uses only that album's samples.
 *
 * @param librosaAlbum  - The factory-generated album name in librosa_min (e.g. "blues_70BPM_5f073160")
 * @param bpm           - BPM of the album (from D1 user_uploads record)
 * @param genre         - Genre of the album (from D1 user_uploads record)
 * @param sizeCategory  - Loop size category (SHORT/MID/LARGE/CUSTOM)
 * @param qdrantService - Qdrant service instance
 * @throws Error prefixed with the album name, so failures of a single pinned album are identifiable
 */
export async function generateFromAlbum(
    librosaAlbum: string,
    bpm: number,
    genre: string,
    sizeCategory: SizeCategory,
    qdrantService: QdrantService,
    customPattern?: Array<Record<string, number>>,
): Promise<LoopResult> {
    try {
        return await _generateFromAlbum(librosaAlbum, bpm, genre, sizeCategory, qdrantService, customPattern);
    } catch (e) {
        throw new Error(`Could not generate from album ${librosaAlbum}: ${(e as Error).message}`);
    }
}

async function _generateFromAlbum(
    librosaAlbum: string,
    bpm: number,
    genre: string,
    sizeCategory: SizeCategory,
    qdrantService: QdrantService,
    customPattern?: Array<Record<string, number>>,
): Promise<LoopResult> {
    if (isDev) { console.log('[FromAlbum] Generating loop from pinned album:', librosaAlbum, 'BPM:', bpm, 'genre:', genre, customPattern ? 'CUSTOM pattern' : sizeCategory); }

    // 1. Fetch sample metadata for the pinned album
    const samples = await qdrantService.getSamplesByAlbum(librosaAlbum);
    if (samples.length === 0) {
        throw new Error(`No samples found for album: ${librosaAlbum}`);
    }
    if (isDev) { console.log('[FromAlbum] Found', samples.length, 'samples'); }

    // 2. Compute bars per sample — snap to nearest musical fraction to absorb float drift
    const pulseCompas = samples[0]?.pulse_compas ?? DEFAULT_PULSE_COMPAS;
    const barTime = barTimeCalc(bpm, pulseCompas, DEFAULT_TIME_REFERENCE);
    const sampleMetas: SampleMeta[] = samples
        .filter(s => s.duration > 0 && s.token)
        .map(s => ({
            file:         s.file,
            bars:         snapToMusicalBars(s.duration / barTime),
            token:        s.token,
            isFill:       s.isFill,
            pulse_compas: pulseCompas,
        }));

    // 3. Build tokenSamples
    const tokenSamples: Record<string, SampleMeta[]> = {};
    for (const sm of sampleMetas) {
        if (!tokenSamples[sm.token]) tokenSamples[sm.token] = [];
        tokenSamples[sm.token].push(sm);
    }
    if (isDev) { console.log('[FromAlbum] Token types:', Object.keys(tokenSamples)); }

    // 4-6. Generate pattern and populate
    let result;
    if (customPattern && customPattern.length > 0) {
        // Custom pattern supplied — use it directly (same as generateCustomPatternLoop)
        if (isDev) { console.log('[FromAlbum] Using custom pattern:', customPattern); }
        result = populateFixedPattern(
            customPattern,
            tokenSamples,
            250,
            COIN_CHANGE_MAX_COMBOS,
            COIN_CHANGE_TOLERANCE,
            isDev,
            isDev ? { album: librosaAlbum, genre, bpm } : undefined,
            PERIODIC_FILL_ENABLED,
            PERIODIC_FILL_EVERY,
            PERIODIC_FILL_PROBABILITY,
        );
    } else {
        // Standard size-based generation
        const validBars: Record<string, number[]> = {};
        for (const [token, metas] of Object.entries(tokenSamples)) {
            const pool = buildSizePool(metas);
            validBars[token] = computeAchievableTargets(pool, 32, COIN_CHANGE_TOLERANCE);
        }

        const style = genre.toLowerCase();
        const mode = SIZE_CATEGORY_TO_MODE[sizeCategory];
        if (isDev) { console.log('[FromAlbum] style:', style, 'mode:', mode); }

        result = generateAndPopulate(
            style, mode, tokenSamples, 250, COIN_CHANGE_MAX_COMBOS, COIN_CHANGE_TOLERANCE, validBars,
            false, false,
            isDev,
            isDev ? { album: librosaAlbum, genre, bpm } : undefined,
            PERIODIC_FILL_ENABLED,
            PERIODIC_FILL_EVERY,
            PERIODIC_FILL_PROBABILITY,
        );
    }

    if (!result.complete) {
        throw new Error(
            `No valid loop found after ${result.attempts} attempts` +
            (result.failedAt ? ` (stuck at ${result.failedAt})` : ''),
        );
    }

    // 7–9. Collect files, fetch audio, decompress and concatenate
    if (isDev) { console.log('[FromAlbum] Fetching audio for populated slots'); }
    const { allFiles, allTokens, allDurations, loopSlots, concatenated, sampleRate, actualDuration } =
        await _collectAndFetchAudio(result, barTime, librosaAlbum, qdrantService);

    // 10. Return LoopResult
    const loopResult: LoopResult = {
        audioData: concatenated,
        album: librosaAlbum,
        genre,
        bpm,
        pulse_compas: pulseCompas,
        files: allFiles,
        segments: allFiles.map((file, i) => ({
            file,
            token: allTokens[i],
            duration: allDurations[i],
        })),
        slots: loopSlots,
        totalDuration: actualDuration,
        mode: 'Advanced',
        sampleRate,
        trace: result.trace,
    };

    if (isDev) {
        console.log('[FromAlbum] Loop generated successfully');
        console.log('[FromAlbum] Structure:', allTokens.join(' -> '));
        console.log('[FromAlbum] Total duration:', actualDuration.toFixed(2), 'seconds');
    }
    return loopResult;
}
